import React, { useState, useEffect } from 'react';
import {
    getPublication,
    getPublicationLogs,
    publishNow,
    cancelPublication
} from '../../services/api';
import type { ScheduledPublication, PublicationLog } from '../../services/api';
import { FileText, Linkedin, Twitter, Users, Instagram, Mail, Globe, Check, X, Zap, XCircle, ExternalLink } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import './SchedulingPage.css';
import './PublicationDetailModal.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
    publicationId: string;
    onClose: () => void;
}

const PublicationDetailModal: React.FC<Props> = ({ publicationId, onClose }) => {
    const [publication, setPublication] = useState<ScheduledPublication | null>(null);
    const [logs, setLogs] = useState<PublicationLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [publishing, setPublishing] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [showLogs, setShowLogs] = useState(false);

    useEffect(() => {
        loadPublication();
        loadLogs();
    }, [publicationId]);

    const loadPublication = async () => {
        try {
            setLoading(true);
            setError(null);
            const pub = await getPublication(publicationId);
            setPublication(pub);
        } catch (err: any) {
            console.error('Failed to load publication:', err);
            setError(getApiError(err, 'Failed to load publication'));
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async () => {
        try {
            const logData = await getPublicationLogs(publicationId);
            setLogs(logData);
        } catch (err: any) {
            console.error('Failed to load logs:', err);
        }
    };

    const handlePublishNow = async () => {
        if (!window.confirm('Publish this content immediately?')) {
            return;
        }

        setPublishing(true);
        setError(null);

        try {
            const result = await publishNow(publicationId);
            if (result.success) {
                alert('Published successfully!');
                await loadPublication();
                await loadLogs();
            } else {
                alert(`Publish failed: ${result.message}`);
            }
        } catch (err: any) {
            console.error('Failed to publish:', err);
            setError(getApiError(err, 'Failed to publish'));
        } finally {
            setPublishing(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Cancel this scheduled publication?')) {
            return;
        }

        setCancelling(true);
        setError(null);

        try {
            await cancelPublication(publicationId);
            alert('Publication cancelled');
            onClose();
        } catch (err: any) {
            console.error('Failed to cancel:', err);
            setError(getApiError(err, 'Failed to cancel publication'));
        } finally {
            setCancelling(false);
        }
    };

    const getStatusColor = (status: string): string => {
        const colors: Record<string, string> = {
            scheduled: '#1e40af',
            publishing: '#f59e0b',
            published: '#16a34a',
            failed: '#dc2626',
            cancelled: '#64748b'
        };
        return colors[status] || '#64748b';
    };

    const getPlatformIcon = (platform: string): React.ReactNode => {
        const iconProps = { size: 18 };
        switch (platform) {
            case 'wordpress': return <FileText {...iconProps} />;
            case 'linkedin': return <Linkedin {...iconProps} />;
            case 'twitter': return <Twitter {...iconProps} />;
            case 'facebook': return <Users {...iconProps} />;
            case 'instagram': return <Instagram {...iconProps} />;
            case 'edm': return <Mail {...iconProps} />;
            default: return <Globe {...iconProps} />;
        }
    };

    if (loading) {
        return (
            <div className="pdm-overlay" onClick={onClose}>
                <div className="pdm-content" onClick={(e) => e.stopPropagation()}>
                    <div className="pdm-loading">Loading publication...</div>
                </div>
            </div>
        );
    }

    if (error || !publication) {
        return (
            <div className="pdm-overlay" onClick={onClose}>
                <div className="pdm-content" onClick={(e) => e.stopPropagation()}>
                    <div className="pdm-error-state">{error || 'Publication not found'}</div>
                    <div className="pdm-footer">
                        <GlassButton variant="secondary" onClick={onClose}>Close</GlassButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pdm-overlay" onClick={onClose}>
            <div className="pdm-content" onClick={(e) => e.stopPropagation()}>

                <div className="pdm-header">
                    <div className="pdm-title">
                        <span>{getPlatformIcon(publication.platform)}</span>
                        <h2>{publication.content_data.title || 'Publication Details'}</h2>
                    </div>
                    <button onClick={onClose} className="pdm-close">&times;</button>
                </div>

                <div className="pdm-body">
                    <div className="pdm-info">
                        <div className="pdm-info-row">
                            <span className="pdm-label">Platform:</span>
                            <span className="pdm-value">{publication.platform.toUpperCase()}</span>
                        </div>
                        <div className="pdm-info-row">
                            <span className="pdm-label">Status:</span>
                            <span className="pdm-status-badge" style={{ backgroundColor: getStatusColor(publication.status) }}>
                                {publication.status}
                            </span>
                        </div>
                        <div className="pdm-info-row">
                            <span className="pdm-label">Scheduled For:</span>
                            <span className="pdm-value">{new Date(publication.scheduled_for).toLocaleString()}</span>
                        </div>
                        {publication.published_at && (
                            <div className="pdm-info-row">
                                <span className="pdm-label">Published At:</span>
                                <span className="pdm-value">{new Date(publication.published_at).toLocaleString()}</span>
                            </div>
                        )}
                        {publication.external_post_url && (
                            <div className="pdm-info-row">
                                <span className="pdm-label">Post URL:</span>
                                <a href={publication.external_post_url} target="_blank" rel="noopener noreferrer" className="pdm-external-link">
                                    View on {publication.platform}
                                </a>
                            </div>
                        )}
                        {publication.error_message && (
                            <div className="pdm-info-row pdm-error-row">
                                <span className="pdm-label">Error:</span>
                                <span className="pdm-error-value">{publication.error_message}</span>
                            </div>
                        )}
                        {publication.retry_count > 0 && (
                            <div className="pdm-info-row">
                                <span className="pdm-label">Retry Count:</span>
                                <span className="pdm-value">{publication.retry_count}</span>
                            </div>
                        )}
                    </div>

                    {publication.content_data.body && (
                        <div className="pdm-preview">
                            <p className="pdm-preview-title">Content Preview</p>
                            <div
                                className="pdm-preview-body"
                                dangerouslySetInnerHTML={{
                                    __html: publication.content_data.body.substring(0, 400) + '…'
                                }}
                            />
                        </div>
                    )}

                    {logs.length > 0 && (
                        <div className="pdm-logs">
                            <button
                                onClick={() => setShowLogs(!showLogs)}
                                className="btn-secondary"
                            >
                                {showLogs ? 'Hide' : 'Show'} Activity Log ({logs.length})
                            </button>
                            {showLogs && (
                                <div className="pdm-logs-list">
                                    {logs.map((log) => (
                                        <div key={log.log_id} className="pdm-log-entry">
                                            <div className="pdm-log-header">
                                                <span className="pdm-log-status" style={{ backgroundColor: getStatusColor(log.status) }}>
                                                    {log.status}
                                                </span>
                                                <span className="pdm-log-time">
                                                    {new Date(log.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                            {log.message && <div className="pdm-log-message">{log.message}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="pdm-footer">
                    {publication.task_id && (
                        <GlassButton
                            variant="secondary"
                            onClick={() => window.open(`/preview/${publication.task_id}`, '_blank')}
                            icon={<ExternalLink size={16} />}
                            title="Open the full article/social post preview (grounding, style and readability badges included)"
                        >
                            Open Full Preview
                        </GlassButton>
                    )}
                    {['scheduled', 'failed', 'published'].includes(publication.status) && (
                        <GlassButton
                            variant="primary"
                            onClick={handlePublishNow}
                            disabled={publishing}
                            loading={publishing}
                            icon={<Zap size={16} />}
                        >
                            {publishing ? 'Publishing...' : 'Publish Now'}
                        </GlassButton>
                    )}
                    {publication.status === 'scheduled' && (
                        <GlassButton
                            variant="danger"
                            onClick={handleCancel}
                            disabled={cancelling}
                            loading={cancelling}
                            icon={<XCircle size={16} />}
                        >
                            {cancelling ? 'Cancelling...' : 'Cancel Publication'}
                        </GlassButton>
                    )}
                    <GlassButton variant="secondary" onClick={onClose}>
                        Close
                    </GlassButton>
                </div>

            </div>
        </div>
    );
};

export default PublicationDetailModal;
